import "./style.scss";

import AccessTime from "@mui/icons-material/AccessTime";
import Visibility from "@mui/icons-material/Visibility";
import { TabContext, TabPanel } from "@mui/lab";
import { FormControl, Modal, TextField } from "@mui/material";
import ArrowRight from "assets/img/svg/arrowRight";
import Bank from "assets/img/svg/bank";
import ModalBox from "components/Modal";
import TrackedButton from "containers/TrackedButton";
import { formatLocaleDateString, formatNumber, maskString } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import type { IPayment } from "store/paymentSlice";
import { downloadSageAccountingFile, selectLastUploadedSageFile } from "store/paymentSlice";
import type { ICompany } from "store/settingsStore";
import { useAppDispatch } from "store/store";
import { useAppSelector } from "store/store";
import { StyledMenuItem, StyledSelect } from "../StyledSelect";
import {
    hasLocalBankInfoComplete as _hasLocalBankInfoComplete,
    isInternationalPayment as _isInternationalPayment,
    deriveDisplayAccountValue as _deriveDisplayAccountValue,
    deriveIbanButtonState as _deriveIbanButtonState,
} from "helpers/paymentExportUtils";
import type { PaymentFormatId } from "types/countryFeatures";

import variables from "../../../assets/styles/_variables.module.scss";
import LastElementTable from "../LastElementTable";
import type { LastElementTableColumns } from "../LastElementTable/type";
import MessageBlock from "../MessageBlock";
import { FileButton } from "../MultiStep/ImportFile";
import StyledButton from "../StyledButton";
import CompanyModal from "./CompanyModal";
import ConfirmDownloadModal from "./ConfirmDownloadModal";
import IbanModal from "./IbanModal";
import SageUploadConfirmModal from "./SageUploadConfirmModal";
import type { SageUploadResultPayload } from "./SageUploadStatus";
import SageUploadStatus from "./SageUploadStatus";

interface PaymentExport {
    payments: IPayment[];
    beneficiariesWithNoBankInfo: IPayment[];
    beneficiariesWithNoAddress?: IPayment[];
    hasIncompleteBeneficiaries?: boolean;
    moveToStep?: any;
    currentTab?: number;
    setCurrentTab?: any;
    close: () => void;
    handleDownload: (
        date: Date,
        downloadMode: "preview" | "pay",
        paymentLotId?: number | null,
        formatId?: PaymentFormatId,
        fileNumber?: number,
    ) => Promise<any>;
    handleSageUpload?: (executionDate: Date, paymentLotId?: number | null, formatId?: PaymentFormatId) => Promise<any>;
    isSageEnabled?: boolean;
    company: ICompany;
    supportedPaymentFormatIds: PaymentFormatId[];
}

const stepButtonStyles = {
    display: "flex",
    gap: "5px",
    minWidth: "120px",
    "&.MuiButton-root": {
        backgroundColor: "#2D2D2D",
        color: "#fff",
        border: "1px solid black",
        borderRadius: "5px",
        "&:hover": {
            backgroundColor: "#4b4b4b",
            borderColor: "#4b4b4b",
        },
        "&.Mui-disabled": {
            color: "white",
            backgroundColor: "rgb(218, 218, 218)",
            borderColor: "rgb(218, 218, 218)",
        },
    },
};

const PaymentExportFlow = ({
    close,
    payments,
    beneficiariesWithNoBankInfo,
    beneficiariesWithNoAddress,
    hasIncompleteBeneficiaries,
    moveToStep,
    currentTab = 0,
    setCurrentTab,
    handleDownload,
    handleSageUpload,
    isSageEnabled = false,
    company,
    supportedPaymentFormatIds,
}: PaymentExport) => {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [modalType, setModalType] = useState("");
    const [currentPayment, setCurrentPayment] = useState<IPayment | null>();
    const [paymentsForExport, setPaymentsForExport] = useState<IPayment[]>();
    const [noBankInfo, setNoBankInfo] = useState<IPayment[]>();
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSageUploading, setIsSageUploading] = useState(false);
    const [sageUploadResult, setSageUploadResult] = useState<SageUploadResultPayload | null>(null);
    const [showSageUploadConfirm, setShowSageUploadConfirm] = useState(false);
    const [selectedFormatId, setSelectedFormatId] = useState<PaymentFormatId>(supportedPaymentFormatIds[0]);
    const [cpaFileNumber, setCpaFileNumber] = useState("");
    const [draftCpaFileNumber, setDraftCpaFileNumber] = useState("");
    const lastUploadedFile = useAppSelector(selectLastUploadedSageFile);
    const date = new Date();

    const isCpa005 = selectedFormatId === "CPA_EFT_005";
    const parsedCpaFileNumber = cpaFileNumber ? parseInt(cpaFileNumber, 10) : undefined;
    const draftParsed = draftCpaFileNumber ? parseInt(draftCpaFileNumber, 10) : undefined;
    const draftError =
        draftCpaFileNumber !== "" && (!/^\d{1,4}$/.test(draftCpaFileNumber) || draftParsed! < 1 || draftParsed! > 9999);

    const handleDraftChange = (val: string) => {
        if (val === "" || /^\d{0,4}$/.test(val)) setDraftCpaFileNumber(val);
    };

    const openCpaFileNumberModal = () => {
        setDraftCpaFileNumber(cpaFileNumber);
        setModalType("cpaFileNumber");
    };

    const confirmCpaFileNumber = () => {
        setCpaFileNumber(draftCpaFileNumber);
        setModalType("");
    };

    const { countryFeatures, internationalBankingEnabled } = useCompanySettings();
    const { internationalPaymentFormatId } = countryFeatures;
    const bankingConfig = { internationalBankingEnabled, bankingFields: countryFeatures.bankingFields };

    const hasLocalBankInfoComplete = (payment: IPayment) => _hasLocalBankInfoComplete(payment, bankingConfig);
    const isInternationalPayment = (payment: IPayment) => _isInternationalPayment(payment, internationalBankingEnabled);
    const deriveDisplayAccountValue = (payment: IPayment) =>
        _deriveDisplayAccountValue(payment, internationalBankingEnabled, countryFeatures.bankingFields);
    const deriveIbanButtonState = (payment: IPayment) => _deriveIbanButtonState(payment, bankingConfig);

    const hasInternationalBeneficiaries =
        internationalBankingEnabled &&
        !!paymentsForExport?.some((p) => p.beneficiary?.bankingStatus === "foreign_international");

    const foreignPaymentsWithNoInternationalFormat = useMemo(() => {
        if (!internationalBankingEnabled || !!internationalPaymentFormatId) return [];
        return (
            paymentsForExport?.filter(
                (p) =>
                    p.beneficiary?.bankingStatus === "foreign_local" ||
                    p.beneficiary?.bankingStatus === "foreign_international",
            ) ?? []
        );
    }, [paymentsForExport, internationalBankingEnabled, internationalPaymentFormatId]);

    const hasForeignBeneficiariesWithNoInternationalFormat = foreignPaymentsWithNoInternationalFormat.length > 0;

    const hasMultipleFormats = supportedPaymentFormatIds.length > 1;
    const finalTab = hasMultipleFormats ? 3 : 2;
    const formatName = t(`PaymentModal.FormatLabels.${selectedFormatId}`);

    // Mirrors the backend check: IBAN info OR bankingMetadata (sort_code/account_number for UK, etc.)
    const hasIbanBankInfo = !!(company.iban && company.bankAccountName);
    const hasBankInfo =
        hasIbanBankInfo || !!(company.bankingMetadata && Object.keys(company.bankingMetadata).length > 0);

    const bankingMeta = company.bankingMetadata as Record<string, string> | null;

    const isBacsSunMissing = selectedFormatId === "BACS_STD18" && !/^\d{6}$/.test(bankingMeta?.bacs_sun ?? "");
    const isUKInfoMissing = isBacsSunMissing;

    const isCpa005BankingMissing =
        selectedFormatId === "CPA_EFT_005" &&
        !(bankingMeta?.institution_number && bankingMeta?.transit_number && bankingMeta?.account_number);
    const isCpa005CompanyNumberMissing = selectedFormatId === "CPA_EFT_005" && !company.siret;
    const isCanadaInfoMissing = isCpa005BankingMissing || isCpa005CompanyNumberMissing;

    const paymentLotId = payments.length ? payments[0].paymentLotId : undefined;

    useEffect(() => {
        const beneficiariesWithError = payments.length > 0 ? beneficiariesWithNoBankInfo : [];
        setNoBankInfo(beneficiariesWithError);
        setPaymentsForExport(payments);
    }, [payments, beneficiariesWithNoBankInfo]);

    const beneficiariesConcerned = useMemo(() => {
        return paymentsForExport?.filter((p) => !isInternationalPayment(p) && hasLocalBankInfoComplete(p));
    }, [paymentsForExport, internationalBankingEnabled, countryFeatures.bankingFields]);

    const handleIban = (payment: IPayment) => {
        setCurrentPayment(payment); // needed for ModalBox
        setModalType("iban");
    };

    const handleIbanClose = () => {
        setModalType("");
        setCurrentPayment(null);
    };

    const renderIban = (_value: string, payment: IPayment) => {
        const buttonState = deriveIbanButtonState(payment);
        const displayValue = deriveDisplayAccountValue(payment);
        return (
            <div>
                {buttonState === "complete" ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                        <span>{maskString(displayValue)}</span>
                        <Visibility
                            sx={{ height: "15px", color: variables["foreground"], cursor: "pointer" }}
                            onClick={() => handleIban(payment)}
                        />
                    </div>
                ) : buttonState === "fix" ? (
                    <button className="payment-iban-btn" onClick={() => handleIban(payment)}>
                        {t("PaymentModal.IbanButtonFix")}
                    </button>
                ) : (
                    <button className="payment-iban-btn" onClick={() => handleIban(payment)}>
                        {t("PaymentModal.IbanButtonEntry")}
                    </button>
                )}
            </div>
        );
    };

    const paymentColumns: LastElementTableColumns[] = [
        {
            id: "name",
            label: t("PaymentModal.Beneficiary"),
            avatar: true,
            sort: false,
            align: "left",
            avatarType: "circular",
        },
        {
            id: "iban",
            label: t("PaymentModal.Iban"),
            avatar: false,
            align: "left",
            sort: false,
            // Use the row itself: looking it up by id fails for rows without an id
            format: (value: any, _id?: number, payment?: IPayment) => (payment ? renderIban(value, payment) : null),
        },
        {
            id: "netRemainingToPay",
            label: t("PaymentModal.NetValueOwed"),
            avatar: false,
            align: "right",
            sort: false,
            format: (value: any) => formatNumber(value / 100, { isCurrency: true, always2Digit: true }),
        },
    ];

    const netTotalPartial = useMemo(() => {
        return beneficiariesConcerned?.reduce((a, { netRemainingToPay }) => {
            return a + netRemainingToPay;
        }, 0);
    }, [beneficiariesConcerned]);

    const internationalBeneficiariesConcerned = useMemo(() => {
        if (!internationalPaymentFormatId) return [];
        return paymentsForExport?.filter((p) => isInternationalPayment(p) && hasLocalBankInfoComplete(p)) ?? [];
    }, [paymentsForExport, internationalBankingEnabled, internationalPaymentFormatId, countryFeatures.bankingFields]);

    const internationalNetTotal = useMemo(() => {
        return internationalBeneficiariesConcerned.reduce((a, { netRemainingToPay }) => a + netRemainingToPay, 0);
    }, [internationalBeneficiariesConcerned]);

    const hasInternationalFile = internationalBeneficiariesConcerned.length > 0;
    const internationalFormatName = t(`PaymentModal.FormatLabels.ISO20022_PAIN001_INTL`);

    // const bottomFooterContent = {
    //     numberOfIssues: noBankInfo?.length,
    //     issuesText: t("PaymentModal.IbanIssuesText"),
    //     rightText: `Total : ${formatNumber(netTotalFull! / 100, { isCurrency: true, always2Digit: true })}`,
    // };

    const bottomFooterContent = (
        <div className="info-footer">
            <div className="info-footer-text">
                <span className="red">
                    {noBankInfo?.length !== 0 ? `${noBankInfo?.length} ${t("PaymentModal.IbanIssuesText")}` : ""}
                </span>
            </div>
        </div>
    );

    const handleCloseModal = () => {
        setCurrentTab(0);
        close();
    };

    const handleDownloadThenClose = async (paymentDate: Date | null) => {
        setIsDownloading(true);
        try {
            // When downloading from press, paymentLotId is null
            const response = await handleDownload(
                paymentDate ?? date,
                "pay",
                paymentLotId,
                selectedFormatId,
                parsedCpaFileNumber,
            );
            if (response.meta.requestStatus === "fulfilled") {
                handleCloseModal();
                toast.success(`${t("PaymentModal.StatusChangedSuccessfully")}`);
            }
        } finally {
            setIsDownloading(false);
        }
    };

    const handleSageUploadClick = () => {
        setShowSageUploadConfirm(true);
    };

    const handleSageUploadConfirm = async () => {
        if (!handleSageUpload) return;
        setIsSageUploading(true);
        try {
            const response = await handleSageUpload(date, paymentLotId, selectedFormatId);
            if (response?.meta?.requestStatus === "fulfilled" && response.payload) {
                const { id, fileName } = response.payload;
                setSageUploadResult({ id, fileName });
                setShowSageUploadConfirm(false);
            }
        } finally {
            setIsSageUploading(false);
        }
    };

    const handleDownloadSageFile = (id: number, fileName: string) => {
        dispatch(downloadSageAccountingFile({ id, fileName }));
    };

    return (
        <div className="payment-export-main">
            <ModalBox
                open={modalType === "iban"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={currentPayment?.name}
                subTitle={t("PaymentModal.IbanModalSubtitle")}
            >
                <IbanModal
                    key={currentPayment?.id}
                    close={() => handleIbanClose()}
                    beneficiaryId={currentPayment?.id ?? -1}
                    setPayments={(data: IPayment[]) => setPaymentsForExport(data)}
                />
            </ModalBox>
            <ModalBox
                open={modalType === "company"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={company.name}
                subTitle={t("PaymentModal.IbanModalSubtitle")}
            >
                <CompanyModal close={() => setModalType("")} company={company} />
            </ModalBox>
            <ModalBox
                open={modalType === "cpaFileNumber"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={t("PaymentModal.Cpa005FileNumberModalTitle")}
                subTitle={t("PaymentModal.Cpa005FileNumberModalSubtitle")}
                style={{ minWidth: 400, maxWidth: 440 }}
            >
                <div className="flex flex-col gap-4 p-2">
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label={t("PaymentModal.Cpa005FileNumber")}
                        value={draftCpaFileNumber}
                        onChange={(e) => handleDraftChange(e.target.value)}
                        error={draftError}
                        helperText={draftError ? t("PaymentModal.Cpa005FileNumberHelper") : ""}
                        inputProps={{ inputMode: "numeric", maxLength: 4 }}
                    />
                    <StyledButton sx={stepButtonStyles} onClick={confirmCpaFileNumber} disabled={draftError}>
                        {t("PaymentModal.Cpa005FileNumberConfirm")}
                    </StyledButton>
                </div>
            </ModalBox>
            <ModalBox
                open={modalType === "noAddress"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={t("PaymentModal.NoAddressModalTitle")}
                subTitle={t("PaymentModal.NoAddressModalSubtitle")}
            >
                <ul className="list-none p-0 m-0 max-h-80 overflow-y-auto">
                    {beneficiariesWithNoAddress?.map((p) => (
                        <li key={p.beneficiary.id} className="py-2 border-b border-gray-100 text-sm">
                            {p.beneficiary.surname} {p.beneficiary.name}
                        </li>
                    ))}
                </ul>
            </ModalBox>
            <ModalBox
                open={modalType === "foreignNoIntl"}
                onClose={() => setModalType("")}
                showCloseButton={true}
                mainTitle={t("PaymentModal.ForeignNoIntlModalTitle")}
                subTitle={t("PaymentModal.ForeignNoIntlModalSubtitle")}
            >
                <ul style={{ listStyle: "none", padding: 0, margin: 0, maxHeight: 320, overflowY: "auto" }}>
                    {foreignPaymentsWithNoInternationalFormat.map((p) => (
                        <li
                            key={p.beneficiary.id}
                            style={{ padding: "8px 0", borderBottom: "1px solid #f0f0f0", fontSize: 14 }}
                        >
                            {p.beneficiary.surname} {p.beneficiary.name}
                        </li>
                    ))}
                </ul>
            </ModalBox>
            <Modal open={modalType === "confirmation"} onClose={() => setModalType("")}>
                <ConfirmDownloadModal
                    onClose={() => setModalType("")}
                    onConfirmation={handleDownloadThenClose}
                    isDownloading={isDownloading}
                />
            </Modal>
            <SageUploadConfirmModal
                open={showSageUploadConfirm}
                onClose={() => setShowSageUploadConfirm(false)}
                onConfirm={handleSageUploadConfirm}
                isUploading={isSageUploading}
            />
            <TabContext value={currentTab.toString()}>
                <TabPanel value="0">
                    <div className="import-flow-0">
                        <div className="import-heading">
                            <h2
                                dangerouslySetInnerHTML={{
                                    __html: t("PaymentModal.MethodTitle"),
                                }}
                            ></h2>
                            {/* <p>{t("PaymentModal.MethodSubtitle")}</p> */}
                        </div>
                        <div className="payment-method-1" onClick={hasBankInfo ? moveToStep(true) : null}>
                            <Bank />
                            <p className="payment-method-title">{t("PaymentModal.PaymentMethodAuto")}</p>
                            <div className="auto-method-button">
                                <div className="auto-method-steps">
                                    <span>{t("PaymentModal.PaymentMethodAuto1")}</span>
                                    <span>{t("PaymentModal.PaymentMethodAuto2")}</span>
                                    <span>{t("PaymentModal.PaymentMethodAuto3")}</span>
                                </div>
                                <ArrowRight color="black" width="20" height="20" />
                            </div>
                        </div>
                        {!hasBankInfo && (
                            <>
                                <MessageBlock type="error" text={t("PaymentModal.FillCompanyBankInfoFirst")} />
                                <button className="company-info-btn" onClick={() => setModalType("company")}>
                                    {t("PaymentModal.CompanyBankFix")}
                                </button>
                            </>
                        )}
                    </div>
                </TabPanel>
                <TabPanel value="1">
                    <div className="import-flow-1">
                        <div className="import-heading">
                            <h2>{t("PaymentModal.PreppingTitle")}</h2>
                            <p>
                                {t(
                                    hasInternationalBeneficiaries && internationalPaymentFormatId
                                        ? "PaymentModal.PreppingSubtitleWithIntl"
                                        : "PaymentModal.PreppingSubtitle",
                                    { formatName },
                                )}
                            </p>
                        </div>
                        <div className={`import-table ${!paymentsForExport?.length ? "empty" : ""}`}>
                            <LastElementTable
                                columns={paymentColumns}
                                data={paymentsForExport}
                                bottomFooterContent={bottomFooterContent}
                                stickyFooter
                                dataType="paymentExports"
                                type="exports"
                                max={9999}
                                maxHeight={300}
                                avatarSourceValue="beneficiaryAvatarUrl"
                            />
                        </div>
                        {noBankInfo?.length || hasIncompleteBeneficiaries ? (
                            <MessageBlock
                                type="warning"
                                text={
                                    hasIncompleteBeneficiaries
                                        ? t("PaymentModal.BeneficiariesWarning")
                                        : t("PaymentModal.BeneficiariesIbanWarning")
                                }
                                style={{ marginTop: "0.5rem" }}
                            />
                        ) : null}
                        {hasForeignBeneficiariesWithNoInternationalFormat && (
                            <MessageBlock
                                type="warning"
                                text={t("PaymentModal.ForeignBeneficiariesNoIntlFormat")}
                                action={() => setModalType("foreignNoIntl")}
                                actionText={t("PaymentModal.ForeignBeneficiariesNoIntlFormatCta")}
                                style={{ marginTop: "0.5rem" }}
                            />
                        )}
                        {!hasMultipleFormats && isUKInfoMissing && (
                            <MessageBlock
                                type="warning"
                                text={t("PaymentModal.BacsSunMissing")}
                                style={{ marginTop: "0.5rem" }}
                            />
                        )}
                        {!hasMultipleFormats && isCanadaInfoMissing && (
                            <MessageBlock
                                type="warning"
                                text={t("PaymentModal.Cpa005InfoMissing")}
                                action={() => setModalType("company")}
                                actionText={t("PaymentModal.Cpa005CompleteCta")}
                                style={{ marginTop: "0.5rem" }}
                            />
                        )}
                        {!!beneficiariesWithNoAddress?.length && payments.length > 0 && (
                            <MessageBlock
                                type="warning"
                                text={t("PaymentModal.BeneficiariesNoAddressWarning")}
                                action={() => setModalType("noAddress")}
                                actionText={t("PaymentModal.BeneficiariesNoAddressCta")}
                                style={{ marginTop: "0.5rem" }}
                            />
                        )}
                    </div>
                </TabPanel>
                {hasMultipleFormats && (
                    <TabPanel value="2">
                        <div className="import-flow-format">
                            <div className="import-heading">
                                <h2>{t("PaymentModal.FormatTitle")}</h2>
                                <p>{t("PaymentModal.FormatSubtitle")}</p>
                            </div>
                            <FormControl fullWidth sx={{ mt: 2 }}>
                                <StyledSelect
                                    value={selectedFormatId}
                                    onChange={(e) => setSelectedFormatId(e.target.value as PaymentFormatId)}
                                >
                                    {supportedPaymentFormatIds.map((id) => (
                                        <StyledMenuItem key={id} value={id}>
                                            {t(`PaymentModal.FormatLabels.${id}`)}
                                        </StyledMenuItem>
                                    ))}
                                </StyledSelect>
                            </FormControl>
                            {isUKInfoMissing && (
                                <MessageBlock
                                    type="warning"
                                    text={t("PaymentModal.BacsSunMissing")}
                                    style={{ marginTop: "1rem" }}
                                />
                            )}
                            {isCanadaInfoMissing && (
                                <MessageBlock
                                    type="warning"
                                    text={t("PaymentModal.Cpa005InfoMissing")}
                                    action={() => setModalType("company")}
                                    actionText={t("PaymentModal.Cpa005CompleteCta")}
                                    style={{ marginTop: "1rem" }}
                                />
                            )}
                        </div>
                    </TabPanel>
                )}
                <TabPanel value={finalTab.toString()}>
                    <div className="import-flow-2">
                        <div className="import-heading">
                            {hasInternationalFile ? (
                                <>
                                    <h2>
                                        {t("PaymentModal.PaymentTitleWithIntl", {
                                            formatName,
                                            internationalFormatName,
                                        })}
                                    </h2>
                                    <p>
                                        {t("PaymentModal.PaymentSubtitleLocalWithIntl", {
                                            count: beneficiariesConcerned?.length,
                                            total: formatNumber(netTotalPartial! / 100, {
                                                isCurrency: true,
                                                always2Digit: true,
                                            }),
                                            formatName,
                                        })}
                                    </p>
                                    <p>
                                        {t("PaymentModal.PaymentSubtitleIntl", {
                                            count: internationalBeneficiariesConcerned.length,
                                            total: formatNumber(internationalNetTotal / 100, {
                                                isCurrency: true,
                                                always2Digit: true,
                                            }),
                                            internationalFormatName,
                                        })}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <h2>{t("PaymentModal.PaymentTitle", { formatName })}</h2>
                                    <p>
                                        {t("PaymentModal.PaymentSubtitle1")} {beneficiariesConcerned?.length}{" "}
                                        {t("PaymentModal.PaymentSubtitle2")}{" "}
                                        {formatNumber(netTotalPartial! / 100, {
                                            isCurrency: true,
                                            always2Digit: true,
                                        })}
                                    </p>
                                </>
                            )}
                            <p>{t("PaymentModal.PaymentSubtitleDisclaimer")}</p>
                        </div>
                        <div className="payment-instructions">
                            <span className="step">
                                <span className="step-title">{t("PaymentModal.Step", { number: 1 })}</span>
                                <span className="step-text">
                                    {" "}
                                    {hasInternationalFile
                                        ? t("PaymentModal.Step1TextWithIntl", { formatName, internationalFormatName })
                                        : t("PaymentModal.Step1Text", { formatName })}{" "}
                                    {t("PaymentModal.ControlInfo")}
                                </span>
                                {isCpa005 && (
                                    <p className="text-sm text-[#141516] mt-2">
                                        {t("PaymentModal.Cpa005FileNumberIntro")}{" "}
                                        <button
                                            className="inline border-none bg-transparent text-red-600 font-medium p-0 cursor-pointer text-sm underline"
                                            onClick={openCpaFileNumberModal}
                                        >
                                            {cpaFileNumber
                                                ? t("PaymentModal.Cpa005FileNumberEdit", { number: cpaFileNumber })
                                                : t("PaymentModal.Cpa005FileNumberCta")}
                                        </button>
                                    </p>
                                )}
                                <div className="step-buttons">
                                    <TrackedButton action="payments-bank-file">
                                        <div className="step-button-file">
                                            <FileButton
                                                label={t("PaymentModal.TransferFiles")}
                                                onClick={() =>
                                                    handleDownload(
                                                        date,
                                                        "preview",
                                                        paymentLotId,
                                                        selectedFormatId,
                                                        parsedCpaFileNumber,
                                                    )
                                                }
                                                buttonColor="white"
                                            />
                                        </div>
                                    </TrackedButton>
                                    {isSageEnabled && handleSageUpload && (
                                        <div className="sage-upload-container">
                                            <SageUploadStatus
                                                isUploading={isSageUploading}
                                                result={sageUploadResult}
                                                onClick={handleSageUploadClick}
                                                sendLabel={t("PaymentModal.SendToSage")}
                                                successLabel={t("PaymentModal.SageUploadSuccess")}
                                                onDownload={handleDownloadSageFile}
                                            />
                                            {lastUploadedFile && !sageUploadResult && (
                                                <button
                                                    className="sage-last-upload-link"
                                                    onClick={() =>
                                                        handleDownloadSageFile(
                                                            lastUploadedFile.id,
                                                            lastUploadedFile.fileName,
                                                        )
                                                    }
                                                    title={t("PaymentModal.SageHistory.DownloadFile")}
                                                >
                                                    <AccessTime className="sage-last-upload-icon" />
                                                    <span className="sage-last-upload-text">
                                                        {t("PaymentModal.LastUploadedFile")}{" "}
                                                        {formatLocaleDateString({
                                                            date: new Date(lastUploadedFile.createdAt),
                                                            options: { numeric: true, showTime: true, timeZone: "UTC" },
                                                        })}
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </span>
                            <span className="step">
                                <span className="step-title">{t("PaymentModal.Step", { number: 2 })}</span>
                                <span className="step-text">
                                    {" "}
                                    {hasInternationalFile
                                        ? t("PaymentModal.Step2TextWithIntl")
                                        : t("PaymentModal.Step2Text")}
                                </span>
                            </span>
                            <span className="step">
                                <span className="step-title">{t("PaymentModal.Step", { number: 3 })}</span>
                                <span className="step-text">
                                    {" "}
                                    {hasInternationalFile
                                        ? t("PaymentModal.Step3TextWithIntl")
                                        : t("PaymentModal.Step3Text")}
                                </span>
                            </span>
                        </div>
                    </div>
                </TabPanel>
            </TabContext>
            <div className="import-flow-btns">
                {currentTab === 1 && (
                    <div className="btn-tab-1">
                        <StyledButton
                            sx={{
                                "&.MuiButton-root": {
                                    border: "1px solid #EAEAEA",
                                    textDecoration: "none",
                                    borderRadius: "5px",
                                },
                            }}
                            onClick={moveToStep(false)}
                        >
                            Retour
                        </StyledButton>
                        {paymentsForExport && paymentsForExport?.length > 0 ? (
                            <StyledButton
                                sx={stepButtonStyles}
                                onClick={moveToStep(true)}
                                disabled={!hasMultipleFormats && (isUKInfoMissing || isCanadaInfoMissing)}
                            >
                                {t("PaymentModal.ButtonContinue")}
                            </StyledButton>
                        ) : (
                            <StyledButton
                                sx={stepButtonStyles}
                                onClick={() => navigate("/management", { state: { tab: 1 } })}
                            >
                                {t("PaymentModal.GoToManagement")}
                            </StyledButton>
                        )}
                    </div>
                )}
                {hasMultipleFormats && currentTab === 2 && (
                    <div className="btn-tab-1">
                        <StyledButton
                            sx={{
                                "&.MuiButton-root": {
                                    border: "1px solid #EAEAEA",
                                    textDecoration: "none",
                                    borderRadius: "5px",
                                },
                            }}
                            onClick={moveToStep(false)}
                        >
                            Retour
                        </StyledButton>
                        <StyledButton
                            sx={stepButtonStyles}
                            onClick={moveToStep(true)}
                            disabled={isUKInfoMissing || isCanadaInfoMissing}
                        >
                            {t("PaymentModal.ButtonContinue")}
                        </StyledButton>
                    </div>
                )}
                {currentTab === finalTab && (
                    <div className="btn-tab-2">
                        <StyledButton
                            sx={{
                                "&.MuiButton-root": {
                                    border: "1px solid #EAEAEA",
                                    textDecoration: "none",
                                    borderRadius: "5px",
                                },
                            }}
                            onClick={moveToStep(false)}
                        >
                            Retour
                        </StyledButton>
                        <TrackedButton action="payments-set-as-paid">
                            <StyledButton sx={stepButtonStyles} onClick={() => setModalType("confirmation")}>
                                {t("PaymentModal.ButtonConfirm")}
                            </StyledButton>
                        </TrackedButton>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PaymentExportFlow;
